import { Link, NavLink, Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import {
  Boxes,
  Copy,
  LayoutDashboard,
  ListChecks,
  Settings as SettingsIcon,
  SlidersHorizontal,
  Sparkles,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/v2/StatusBadge";
import { useDuplicateService, useServiceDetail } from "@/lib/service-detail";
import { useToast } from "@/components/v2/Toast";

/**
 * Pencil component `Qp9lm` (`v2/ServiceDetail`).
 *
 * Shell for `/services/:id/*`:
 *   - Header (name + status pill + Duplicate / Edit Service actions).
 *     Breadcrumb lives in the AppShell topbar — duplicating it inside
 *     the page chrome made the same trail render twice.
 *   - LEFT vertical tab rail (Overview / Bookings / Clients / Inventory /
 *     Custom Fields / Settings) — same chrome pattern as `SettingsLayout`.
 *   - Right pane mounts the active sub-route via `<Outlet />`.
 */

const TABS = [
  { to: "overview", icon: LayoutDashboard, label: "Overview" },
  { to: "bookings", icon: ListChecks, label: "Bookings" },
  { to: "clients", icon: Users, label: "Clients" },
  { to: "inventory", icon: Boxes, label: "Inventory" },
  { to: "custom-fields", icon: SlidersHorizontal, label: "Custom Fields" },
  { to: "settings", icon: SettingsIcon, label: "Settings" },
];

export function ServiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const detail = useServiceDetail(id);
  const duplicate = useDuplicateService();

  if (!id) return <Navigate to="/services" replace />;

  if (detail.isError) {
    return (
      <div className="px-6 py-10">
        <Link to="/services" className="text-sm text-muted-foreground hover:text-foreground">
          ← Services
        </Link>
        <p className="mt-4 text-sm text-destructive">
          {(detail.error as Error).message}
        </p>
      </div>
    );
  }

  const service = detail.data?.base;
  const fieldCount = detail.data?.customFields?.length ?? 0;
  const status = service?.active ? "active" : "draft";

  return (
    <div className="px-6 py-6 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-primary-light text-primary inline-flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight truncate">
                {service?.name ?? "Loading…"}
              </h1>
              {service && <StatusBadge label={status} />}
            </div>
            {service && (
              <p className="text-xs text-muted-foreground mt-1 tabular-nums">
                {fieldCount} custom {fieldCount === 1 ? "field" : "fields"}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!service || duplicate.isPending}
            onClick={async () => {
              if (!service) return;
              try {
                const copy = await duplicate.mutateAsync(service.id);
                toast.success("Service duplicated", "Saved as draft.");
                navigate(`/services/${copy.id}/overview`);
              } catch (e) {
                toast.error("Couldn't duplicate", (e as Error).message);
              }
            }}
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" />
            {duplicate.isPending ? "Duplicating…" : "Duplicate"}
          </Button>
        </div>
      </header>

      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        <nav className="space-y-0.5">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted/60"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{t.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
